import {useContext, useEffect, useState} from "react";
import {useMessages} from "i18n";
import {ToastContext} from "Toast";
import {useUser} from "UserProvider";
import {renderedFailureMessage, RenderedFailureKey} from "utils/renderedFailure";
import {useKeyedSingleFlight} from "utils/useSingleFlight";
import {canUseCustomerBranding, planOf} from "home/gating";
import {compressPickedImage} from "core/images/compressPickedImage";
import {ImageCompressionError} from "core/images/imageCompression";
import {LOGO_IMAGE_TARGET} from "core/images/uploadImageTargets";
import {AccountBrandApi, type CustomerBrand} from "../AccountBrandApi";
import {readableForegroundFor} from "../brand/brandColors";
import {validateBrandColor, validateBrandLogo} from "../brand/brandValidation";
import {useAccountAppColorPublisher} from "../brand/AccountAppColorContext";

/**
 * Contract of {@link useAccountBrand} — the thin React adapter over `/account/brand` (api#98). Every
 * rule it applies lives in `account/brand/brandValidation.ts` and `account/brand/brandColors.ts`; this
 * hook only sequences requests and surfaces toasts (epic #26).
 */
export interface UseAccountBrand {
	/** Whether the plan includes customer branding. `false` renders the card's upsell instead of the editor. */
	entitled: boolean;
	/** The saved brand, or an all-null placeholder until the first load resolves. */
	brand: CustomerBrand;
	/** The colour currently in the picker — the saved one until the sender moves it. */
	color: string;
	/** The foreground the recipient page will paint on {@link color}, derived exactly as the api does. */
	onColor: string;
	/** True while a request is in flight; the card disables its controls. */
	busy: boolean;
	/** True when {@link color} differs from the saved accent, so Save is meaningful. */
	canSave: boolean;
	/** True when there is anything to remove. */
	canReset: boolean;
	setColor: (value: string) => void;
	saveColor: () => Promise<void>;
	uploadLogo: (logo: File) => Promise<void>;
	reset: () => Promise<void>;
}

/** The theme-neutral starting accent for a sender who has not chosen one yet. */
const DEFAULT_COLOR = "#0b3d91";

const EMPTY: CustomerBrand = {logoUrl: null, primaryColor: null, onPrimaryColor: null};

export function useAccountBrand(): UseAccountBrand {
	const m = useMessages();
	const [user] = useUser();
	const {showErrorToast, showSuccessToast} = useContext(ToastContext);
	const [brand, setBrand] = useState<CustomerBrand>(EMPTY);
	const [color, setColor] = useState<string>(DEFAULT_COLOR);
	const [busy, setBusy] = useState(false);
	const entitled = canUseCustomerBranding(planOf(user));
	// Every brand this sheet loads or saves also themes the app (ui#2935), so a new colour shows on the
	// buttons behind the sheet the moment it is saved rather than on the next page load.
	const publishToApp = useAccountAppColorPublisher();

	// Load once. Non-entitled senders skip the request entirely: the api would answer, but there is
	// nothing for them to see and a 4xx on a settings tab is noise.
	useEffect(() => {
		if (!entitled) {
			return;
		}
		let active = true;
		(async () => {
			try {
				const loaded = await AccountBrandApi.getBrand();
				if (!active) {
					return;
				}
				setBrand(loaded);
				publishToApp(loaded);
				setColor(loaded.primaryColor ?? DEFAULT_COLOR);
			} catch (e) {
				showErrorToast(renderedFailureMessage(
					e, RenderedFailureKey.LoadFailed, m.errors, "account_brand_load"));
			}
		})();
		return () => {
			active = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [entitled]);

	// `busy` disables the controls, but only a render after the click. Keys are per ACTION so saving a
	// colour never blocks a logo upload (ui#360).
	const flight = useKeyedSingleFlight();

	/**
	 * @param unprocessable copy for a request whose image could not be compressed. Only the logo upload
	 *   passes it, because it is the only request that touches an image at all.
	 */
	const run = (
		key: string,
		request: () => Promise<CustomerBrand>,
		success: string,
		unprocessable?: string,
	): Promise<void> => flight.run(key, async () => {
		setBusy(true);
		try {
			const saved = await request();
			setBrand(saved);
			publishToApp(saved);
			setColor(saved.primaryColor ?? DEFAULT_COLOR);
			showSuccessToast(success);
		} catch (e) {
			showErrorToast(unprocessable && e instanceof ImageCompressionError
				? unprocessable
				: renderedFailureMessage(
					e, RenderedFailureKey.SaveFailed, m.errors, `account_brand_${key}`));
		} finally {
			setBusy(false);
		}
	});

	const saveColor = async () => {
		const verdict = validateBrandColor(color);
		if (!verdict.valid) {
			showErrorToast(m.account.brand.errors[verdict.error]);
			return;
		}
		await run("color", () => AccountBrandApi.setColor(color), m.account.brand.toasts.saved);
	};

	const uploadLogo = async (logo: File) => {
		const verdict = validateBrandLogo(logo);
		if (!verdict.valid) {
			showErrorToast(m.account.brand.errors[verdict.error]);
			return;
		}
		// INSIDE the flight, not before it. Shrinking a large photo is the slow part of this action, so
		// doing it outside would leave the sheet looking idle for seconds AND would defeat the
		// single-flight guard (ui#360): every one of a sender's repeated clicks would start its own
		// compression. The api's 512 KiB ceiling is unchanged — this only produces what would otherwise
		// have been a 400, and a logo already inside it is passed through untouched (ui#601).
		await run(
			"logo",
			async () => AccountBrandApi.setLogo(await compressPickedImage(logo, LOGO_IMAGE_TARGET)),
			m.account.brand.toasts.saved,
			m.account.brand.errors.logoUnprocessable,
		);
	};

	const reset = async () => {
		await run("reset", () => AccountBrandApi.resetBrand(), m.account.brand.toasts.removed);
	};

	return {
		entitled,
		brand,
		color,
		onColor: readableForegroundFor(color),
		busy,
		canSave: color !== brand.primaryColor,
		canReset: Boolean(brand.logoUrl) || Boolean(brand.primaryColor),
		setColor,
		saveColor,
		uploadLogo,
		reset,
	};
}
